/*
 * The arithmetic behind the date picker.
 *
 * Apart from the component so it can be tested: the test runner strips types
 * rather than compiling, so anything it imports cannot sit in a file with JSX
 * in it. Days are built and read in UTC — a grid built in local time puts the
 * 1st of the month in the previous month for every reader east of Greenwich
 * after lunch, which only shows up in someone else's timezone.
 */

/** A day as the wire spells it: YYYY-MM-DD. */
export function isoDay(d: Date): string {
  return [
    String(d.getUTCFullYear()).padStart(4, '0'),
    String(d.getUTCMonth() + 1).padStart(2, '0'),
    String(d.getUTCDate()).padStart(2, '0'),
  ].join('-');
}

/**
 * The reader's own today, as YYYY-MM-DD.
 *
 * Read from the local calendar, not from the instant's UTC day. Those are
 * different dates for part of every day east of Greenwich: at half past
 * midnight in Mumbai it is still yesterday in UTC, so a field whose earliest
 * day came from `toISOString()` went on offering a day that had already gone.
 *
 * The grid either side of this stays UTC on purpose — a YYYY-MM-DD there is a
 * label rather than an instant — and only "which day is today" is local.
 */
export function todayIso(now: Date = new Date()): string {
  return [
    String(now.getFullYear()).padStart(4, '0'),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
}

/**
 * The UTC instant a day the reader picked starts, read on the reader's own
 * calendar. Owner, 2026-10-12: every date a person enters is meant in their
 * device's zone, and the database keeps UTC. So 13 Oct picked in Mumbai is
 * sent as 2026-10-12T18:30:00.000Z, not as UTC midnight.
 */
export function localDayStart(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0).toISOString();
}

/**
 * A start the reader picked as a day, as the UTC instant to send: their own
 * midnight, or now when that midnight has already passed. "Today" means from
 * now, and the server refuses a start before its own (UTC) today.
 */
export function localDayStartOrNow(iso: string, now: Date = new Date()): string {
  const start = localDayStart(iso);
  const at = new Date(start);
  if (Number.isNaN(at.getTime())) return start;
  return at.getTime() < now.getTime() ? now.toISOString() : start;
}

/**
 * A scheduled start for a day the reader picked, as the UTC instant to send.
 * When a change is already scheduled on that same day of the reader's
 * calendar, its own instant comes back, so the server sees the same start
 * and replaces it (§4.2c) rather than stacking a few hours' worth of value in
 * front of it. Otherwise the reader's own midnight.
 */
export function scheduledStartFor(iso: string, alreadyScheduled?: string | null): string {
  if (alreadyScheduled && localDayOf(alreadyScheduled) === iso) return alreadyScheduled;
  return localDayStart(iso);
}

/** The UTC instant of the last second of a day the reader picked, on their own calendar. */
export function localDayEnd(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59, 0).toISOString();
}

/** The reader's own calendar day an instant falls on, as YYYY-MM-DD. */
export function localDayOf(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : todayIso(d);
}

/** A YYYY-MM-DD day as a Date at UTC midnight. */
export const dayFrom = (iso: string): Date => new Date(`${iso}T00:00:00Z`);

/**
 * The day `by` days away, as YYYY-MM-DD.
 *
 * Calendar arithmetic, not instant arithmetic: a period boundary is a day on
 * a calendar, so this goes through UTC midnight and comes back out as a day
 * string, and no reader's zone touches it on the way. Month and year ends are
 * `Date`'s to get right, which is the reason not to do it with string maths.
 */
export function shiftDay(iso: string, by: number): string {
  const d = dayFrom(iso);
  if (Number.isNaN(d.getTime())) return iso;
  d.setUTCDate(d.getUTCDate() + by);
  return isoDay(d);
}

/**
 * The days a stored period covers, as YYYY-MM-DD.
 *
 * Two traps, and every screen that shows a period range fell into both.
 *
 * The window is half-open: it ends at the first instant *outside* it, so the
 * day a reader would name as its last is the day before `ends_at`. Rendering
 * `ends_at` puts a day in the range the period does not contain — on a
 * statement, a permanent document claiming to cover a day it does not.
 *
 * And a boundary is a calendar day, not an instant. Passing it through a
 * date formatter converts it into the reader's own zone, so `2026-09-01T00:00Z`
 * renders as 31 August to anybody west of Greenwich: a September statement
 * that says it started in August.
 *
 * So the date part is taken off the string and never converted, which is what
 * {@link formatDay} then expects.
 *
 * The third trap, and the one the half-open rule above walks straight into:
 * not every period ends at a midnight. §22.1 lets an operator close a period
 * at the moment they close it, and a same-day period created that way ends at,
 * say, `2026-09-28T06:20:54Z`. Stepping unconditionally back a day then names
 * 27 September as the last day of a period that starts on the 28th — a range
 * that runs backwards, under a note reading "both days included", on the
 * Periods table, on a statement and on an invoice. So the step back belongs to
 * a midnight end only: any other instant is inside the day it names, and that
 * day is the last one the period covers.
 */
export function periodDays(startsAt: string, endsAt: string): { first: string; last: string } {
  const day = endsAt.slice(0, 10);
  const endsAtMidnight = /^T00:00(:00(\.0+)?)?(Z|[+-]\d{2}:\d{2})?$/.test(endsAt.slice(10));
  return { first: startsAt.slice(0, 10), last: endsAtMidnight ? shiftDay(day, -1) : day };
}

/**
 * The 42 days one month's grid shows: the month itself, and the days either
 * side that fill its first and last weeks. Always six rows, so the panel does
 * not change height as the reader pages through months.
 */
export function monthGrid(year: number, month: number): readonly string[] {
  const first = new Date(Date.UTC(year, month, 1));
  const start = new Date(first);
  start.setUTCDate(1 - first.getUTCDay());
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    return isoDay(d);
  });
}

/** The month `by` months from this one, carrying the year with it. */
export function shiftMonth(year: number, month: number, by: number): { year: number; month: number } {
  const d = new Date(Date.UTC(year, month + by, 1));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() };
}

/** Whether a day is inside the caller's bounds — either may be absent. YYYY-MM-DD compares as text. */
export function dayInRange(iso: string, min?: string, max?: string): boolean {
  if (min && iso < min) return false;
  if (max && iso > max) return false;
  return true;
}
